// /Users/aminajackson/Git-projects/pro-maximizer/frontend/src/Sidebar.tsx

import React from 'react';
import { useAppContext, Activity } from './AppContext';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  ListSubheader,
  IconButton,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Home as HomeIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Description as DescriptionIcon,
  RocketLaunch as RocketLaunchIcon,
  Dashboard as DashboardIcon,
  ViewKanban as ViewKanbanIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  DeleteSweep as DeleteSweepIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';

// --- Type Definitions ---
// SUGGESTION: Define a type for navigation items for better type safety.
interface NavItem {
  key: string;
  text: string;
  icon: React.ReactElement;
}

// --- Constants ---
// Helper to map icon names from the activity object to actual icon components
const ICONS: { [key: string]: React.ReactElement } = {
  AddCircleOutline: <AddCircleOutlineIcon fontSize="small" />,
  Settings: <SettingsIcon fontSize="small" />,
  ViewKanban: <ViewKanbanIcon fontSize="small" />,
  Description: <DescriptionIcon fontSize="small" />,
  Group: <GroupIcon fontSize="small" />,
  PersonAdd: <PersonAddIcon fontSize="small" />,
  Edit: <EditIcon fontSize="small" />,
};

// SUGGESTION: Define constant arrays outside the component to prevent re-creation on every render.
const MAIN_NAV_ITEMS: NavItem[] = [
  { key: 'home', text: 'Home', icon: <HomeIcon /> },
  { key: 'newProject', text: 'New Project', icon: <AddCircleOutlineIcon /> },
  { key: 'projects', text: 'Projects', icon: <DescriptionIcon /> },
  { key: 'develop', text: 'Develop', icon: <RocketLaunchIcon /> },
  { key: 'dashboards', text: 'Dashboards', icon: <DashboardIcon /> },
  { key: 'boards', text: 'Boards', icon: <ViewKanbanIcon /> },
  { key: 'collab', text: 'Collab Space', icon: <GroupIcon /> },
];

const SECONDARY_NAV_ITEMS: NavItem[] = [
  { key: 'settings', text: 'Settings', icon: <SettingsIcon /> },
];

// Helper to format the timestamp into a user-friendly string like "5m ago"
const formatTimestamp = (isoString: string): string => {
    const date = new Date(isoString);
    const now = new Date();
    const diffSeconds = Math.round((now.getTime() - date.getTime()) / 1000);

    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    const diffMinutes = Math.round(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.round(diffHours / 24);
    return `${diffDays}d ago`;
}

interface SidebarProps {
  selectedItem: string;
  onSelectItem: (item: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ selectedItem, onSelectItem }) => {
  const { recentActivities, clearActivities } = useAppContext();

  const renderNavButton = (item: NavItem) => (
    <ListItem key={item.key} disablePadding>
      <ListItemButton
        selected={selectedItem === item.key}
        onClick={() => onSelectItem(item.key)}
        sx={{
          mx: 1,
          my: 0.5,
          borderRadius: 1,
          '&.Mui-selected': {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            '&:hover': {
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
            },
            '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
              color: 'primary.main',
              fontWeight: '600',
            },
          },
        }}
      >
        <ListItemIcon>{item.icon}</ListItemIcon>
        <ListItemText primary={item.text} />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Box
      sx={{
        width: 240,
        flexShrink: 0,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          component="img"
          src="/intechgambit-logo.png"
          alt="IntechGambit Logo"
          sx={{ height: 40, width: 40, objectFit: 'contain' }}
        />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            whiteSpace: 'nowrap',
            color: 'primary.main',
          }}
        >
          Pro-Maximizer
        </Typography>
      </Box>
      <Divider />
      <List sx={{ flexGrow: 1, overflowY: 'auto', px: 1 }}>
        {MAIN_NAV_ITEMS.map(renderNavButton)}
      </List>

      {/* --- Recent Activities Section --- */}
      <List
        sx={{ maxHeight: 220, overflowY: 'auto' }}
        subheader={
          <ListSubheader
            component="div"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: 'transparent',
              color: 'text.secondary',
              fontWeight: '600',
              fontSize: '0.8rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              lineHeight: '2.5',
              px: 2,
            }}
          >
            Recent
            {recentActivities.length > 0 && (
              <Tooltip title="Clear Recent Activities">
                <IconButton onClick={clearActivities} size="small">
                  <DeleteSweepIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </ListSubheader>
        }
      >
        {recentActivities.length > 0 ? (
          recentActivities.map((activity: Activity) => (
            <ListItem key={`${activity.timestamp}-${activity.message}`} disablePadding>
              <ListItemButton sx={{ py: 0.5, mx: 1, borderRadius: 1 }}>
                {activity.icon && ICONS[activity.icon] && (
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {ICONS[activity.icon]}
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={activity.message}
                  secondary={formatTimestamp(activity.timestamp)}
                  primaryTypographyProps={{ variant: 'body2', noWrap: true, title: activity.message }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItemButton>
            </ListItem>
          ))
        ) : (
          <ListItem>
            <ListItemText
              secondary="No recent activities."
              secondaryTypographyProps={{ variant: 'caption', textAlign: 'center', width: '100%', fontStyle: 'italic' }}
            />
          </ListItem>
        )}
      </List>
      <Divider />
      <List sx={{ px: 1 }}>
        {SECONDARY_NAV_ITEMS.map(renderNavButton)}
      </List>
    </Box>
  );
};

export default Sidebar;